import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Alert,
  CircularProgress,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Tooltip,
  InputAdornment,
  Tabs,
  Tab,
  FormControlLabel,
  Checkbox,
  Divider,
  TablePagination,
} from '@mui/material';
import {
  Add,
  Search,
  FilterList,
  MoreVert,
  Edit,
  Delete,
  Block,
  CheckCircle,
  VpnKey,
  Person,
  Email,
  Phone,
  Gavel,
  SupervisorAccount,
  BusinessCenter,
  AssignmentInd,
  WorkOutline,
  Refresh,
  History,
  Upload,
  ContentCopy,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import { getApiErrorMessage, toUserErrorMessage } from '../../utils/apiError';
import ListLoadErrorAlert from '../../components/Common/ListLoadErrorAlert';
import {
  permissionsToArray,
  TEAM_PERMISSION_OPTIONS,
  FIRM_ADMIN_DUAL_ROLE_FLAGS,
  FIRM_ADMIN_DUAL_ROLE_LABELS,
} from '../../utils/permissions';
import { useAuth } from '../../contexts/AuthContext';
import { getAuthToken } from '../../lib/authToken';

// Helper function for API calls
const apiCall = async (url: string, options: RequestInit = {}) => {
  const token = getAuthToken();
  if (!token) {
    throw new Error('No authentication token found. Please log in again.');
  }
  
  const response = await fetch(url, {
    ...options,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'API request failed');
  }
  
  return response.json();
};

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  isOfficeManager?: boolean;
  isManagingAttorney?: boolean;
  phone?: string;
  isActive: boolean;
  lastLoginAt?: string;
  specialization?: string;
  barNumber?: string;
  hourlyRate?: number;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  permissions?: string[];
}

interface UserFormData {
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  isOfficeManager: boolean;
  isManagingAttorney: boolean;
  phone: string;
  specialization: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  isActive: boolean;
  permissions: string[];
}

const initialFormData: UserFormData = {
  firstName: '',
  lastName: '',
  email: '',
  role: 'assistant',
  isOfficeManager: false,
  isManagingAttorney: false,
  phone: '',
  specialization: '',
  address: '',
  city: '',
  state: '',
  zipCode: '',
  isActive: true,
  permissions: [],
};

/**
 * Roles that can additionally hold firm-administrator duties. Mirrors the backend
 * flag map; the copy differs per role because the real-world job differs.
 */
const DUAL_ROLE_OPTIONS: Record<
  string,
  { flag: 'isOfficeManager' | 'isManagingAttorney'; label: string; description: string }
> = {
  paralegal: {
    flag: 'isOfficeManager',
    label: 'Paralegal working as office manager',
    description: 'For small firms where this paralegal also performs legal office manager duties.',
  },
  lawyer: {
    flag: 'isManagingAttorney',
    label: 'Attorney is also the firm administrator',
    description:
      'For small firms where this attorney owns or manages the practice and administers the system as well.',
  },
};

const roleConfig: Record<string, { label: string; color: string; icon: React.ReactElement; description: string }> = {
  admin: {
    label: 'Administrator',
    color: '#9c27b0',
    icon: <SupervisorAccount />,
    description: 'Full access to all system features and settings',
  },
  lawyer: {
    label: 'Attorney',
    color: '#1976d2',
    icon: <Gavel />,
    description: 'Case management, client interaction, billing',
  },
  paralegal: {
    label: 'Paralegal',
    color: '#2e7d32',
    icon: <BusinessCenter />,
    description: 'Document preparation, research, case support',
  },
  assistant: {
    label: 'Legal Assistant',
    color: '#ed6c02',
    icon: <AssignmentInd />,
    description: 'Administrative tasks, scheduling, client communication',
  },
};

const permissionOptions = TEAM_PERMISSION_OPTIONS;

const UsersPage: React.FC = () => {
  const { user: authUser } = useAuth();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<UserFormData>(initialFormData);
  const [activeTab, setActiveTab] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuUser, setMenuUser] = useState<User | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [resetPasswordConfirmOpen, setResetPasswordConfirmOpen] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  const [activityUser, setActivityUser] = useState<User | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importCsv, setImportCsv] = useState('');
  const [importSubmitting, setImportSubmitting] = useState(false);
  const [importResult, setImportResult] = useState<{
    created: number;
    skipped: number;
    errors: { line?: number; email?: string; message?: string; error?: string }[];
    credentials?: { email: string; temporaryPassword: string }[];
  } | null>(null);
  const [tempPasswordInfo, setTempPasswordInfo] = useState<{
    email: string;
    name?: string;
    temporaryPassword: string;
    emailSent?: boolean;
    context: 'create' | 'reset';
  } | null>(null);

  const parseImportLines = (text: string) => {
    const lines = text
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean);
    return lines.map((line, index) => {
      const parts = line.split(',').map((p) => p.trim());
      const [email, firstName, lastName, role] = parts;
      const validEmail = email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      const validRole = !role || Object.keys(roleConfig).includes(role);
      return {
        line: index + 1,
        email: email || '',
        firstName: firstName || '',
        lastName: lastName || '',
        role: role || 'assistant',
        valid: Boolean(validEmail && firstName && lastName && validRole),
        error: !validEmail
          ? 'Invalid email'
          : !firstName || !lastName
            ? 'First and last name required'
            : !validRole
              ? 'Invalid role'
              : undefined,
      };
    });
  };

  const importPreview = parseImportLines(importCsv);

  const handleBulkImport = async () => {
    const validRows = importPreview.filter((r) => r.valid);
    if (validRows.length === 0) {
      toast.error('No valid rows to import');
      return;
    }
    setImportSubmitting(true);
    setImportResult(null);
    try {
      const res = await fetch('/api/users/bulk-import', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          users: validRows.map(({ email, firstName, lastName, role }) => ({
            email,
            firstName,
            lastName,
            role,
          })),
        }),
      });
      if (!res.ok) {
        throw new Error(await getApiErrorMessage(res, 'Bulk import failed.'));
      }
      const json = await res.json();
      const data = json.data || json;
      const createdRows = Array.isArray(data.created) ? data.created : [];
      const skippedRows = Array.isArray(data.skipped) ? data.skipped : [];
      const credentials = createdRows
        .filter((u: any) => u.temporaryPassword)
        .map((u: any) => ({ email: u.email, temporaryPassword: u.temporaryPassword }));
      setImportResult({
        created: createdRows.length || Number(data.createdCount) || 0,
        skipped: skippedRows.length || Number(data.skippedCount) || 0,
        errors: data.errors ?? [],
        credentials,
      });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success(`Import complete: ${createdRows.length || 0} created`);
    } catch (err) {
      toast.error(toUserErrorMessage(err, 'Bulk import failed.'));
    } finally {
      setImportSubmitting(false);
    }
  };

  // Fetch users — tenantId MUST be in the key so a prior firm's cache cannot
  // be served after an in-tab account switch (see AuthContext cache clear).
  const { data: usersData, isLoading, error, refetch } = useQuery({
    queryKey: ['users', authUser?.tenantId, page, rowsPerPage, searchQuery, roleFilter, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', String(page + 1));
      params.append('limit', String(rowsPerPage));
      if (searchQuery) params.append('search', searchQuery);
      if (roleFilter) params.append('role', roleFilter);
      if (statusFilter) params.append('status', statusFilter);
      
      return apiCall(`/api/users?${params.toString()}`);
    },
    enabled: Boolean(authUser?.tenantId),
  });

  // User activity (audit trail)
  const { data: activityData, isLoading: activityLoading } = useQuery({
    queryKey: ['user-activity', activityUser?.id],
    queryFn: async () => {
      return apiCall(`/api/audit/user/${activityUser!.id}?limit=50`);
    },
    enabled: activityOpen && Boolean(activityUser?.id),
  });

  // Create user mutation
  const createUserMutation = useMutation({
    mutationFn: async (data: UserFormData) => {
      return apiCall('/api/users', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },
    onSuccess: (response: any) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      const created = response?.data || response;
      if (created?.temporaryPassword) {
        setTempPasswordInfo({
          email: created.email,
          name: `${created.firstName || ''} ${created.lastName || ''}`.trim(),
          temporaryPassword: created.temporaryPassword,
          emailSent: Boolean(created.emailSent),
          context: 'create',
        });
        toast.success('User created — copy the temporary password to sign in');
      } else {
        toast.success(response?.message || 'User created successfully. Temporary password sent via email.');
      }
      handleCloseDialog();
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create user');
    },
  });

  // Update user mutation
  const updateUserMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UserFormData }) => {
      return apiCall(`/api/users/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('User updated successfully');
      handleCloseDialog();
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update user');
    },
  });

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiCall(`/api/users/${id}`, {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('User deleted successfully');
      setDeleteConfirmOpen(false);
      setMenuUser(null);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete user');
    },
  });

  // Suspend/Activate user mutation
  const toggleUserStatusMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      return apiCall(`/api/users/${id}/suspend`, {
        method: 'PATCH',
        body: JSON.stringify({ isActive }),
      });
    },
    onSuccess: (data: { message: string }) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success(data.message);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update user status');
    },
  });

  // Reset password mutation
  const resetPasswordMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiCall(`/api/users/${id}/reset-password`, {
        method: 'POST',
      });
    },
    onSuccess: (response: any) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setResetPasswordConfirmOpen(false);
      const data = response?.data || response;
      if (data?.temporaryPassword) {
        setTempPasswordInfo({
          email: data.email || menuUser?.email || '',
          name: menuUser ? `${menuUser.firstName} ${menuUser.lastName}` : undefined,
          temporaryPassword: data.temporaryPassword,
          emailSent: Boolean(data.emailSent),
          context: 'reset',
        });
        toast.success('Password reset — copy the temporary password');
      } else {
        toast.success(response?.message || 'Password reset successfully. New temporary password sent via email.');
      }
      setMenuUser(null);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to reset password');
    },
  });

  const handleOpenDialog = async (mode: 'create' | 'edit', user?: User) => {
    setDialogMode(mode);
    if (mode === 'edit' && user) {
      setSelectedUser(user);
      // Prefill from list row immediately, then refresh from GET /:id for full profile
      setFormData({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        role: user.role,
        isOfficeManager: Boolean(user.isOfficeManager),
        isManagingAttorney: Boolean(user.isManagingAttorney),
        phone: user.phone || '',
        specialization: user.specialization || '',
        address: user.address || '',
        city: user.city || '',
        state: user.state || '',
        zipCode: user.zipCode || '',
        isActive: user.isActive,
        permissions: permissionsToArray(user.permissions as any),
      });
      setActiveTab(0);
      setDialogOpen(true);
      try {
        const response = await fetch(`/api/users/${user.id}`, {
          headers: { Authorization: `Bearer ${getAuthToken()}` },
        });
        if (response.ok) {
          const json = await response.json();
          const full = json.data || json;
          setSelectedUser(full);
          setFormData({
            firstName: full.firstName || '',
            lastName: full.lastName || '',
            email: full.email || '',
            role: full.role || user.role,
            isOfficeManager: Boolean(full.isOfficeManager),
            isManagingAttorney: Boolean(full.isManagingAttorney),
            phone: full.phone || '',
            specialization: full.specialization || '',
            address: full.address || '',
            city: full.city || '',
            state: full.state || '',
            zipCode: full.zipCode || '',
            isActive: full.isActive !== false,
            permissions: permissionsToArray(full.permissions),
          });
        }
      } catch {
        /* list row prefill is enough if detail fetch fails */
      }
    } else {
      setSelectedUser(null);
      setFormData(initialFormData);
      setActiveTab(0);
      setDialogOpen(true);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedUser(null);
    setFormData(initialFormData);
    setActiveTab(0);
  };

  const handleFormChange = (field: keyof UserFormData, value: any) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      // A dual-role flag only applies to its own role, so switching roles must
      // clear the others or the checkbox stays checked but silently inert.
      if (field === 'role') {
        const keep = FIRM_ADMIN_DUAL_ROLE_FLAGS[value as string];
        for (const flag of Object.values(FIRM_ADMIN_DUAL_ROLE_FLAGS)) {
          if (flag !== keep) next[flag] = false;
        }
      }
      return next;
    });
  };

  const handlePermissionToggle = (permission: string) => {
    setFormData((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter((p) => p !== permission)
        : [...prev.permissions, permission],
    }));
  };

  const handleSubmit = () => {
    if (dialogMode === 'create') {
      createUserMutation.mutate(formData);
    } else if (selectedUser) {
      updateUserMutation.mutate({ id: selectedUser.id, data: formData });
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, user: User) => {
    setAnchorEl(event.currentTarget);
    setMenuUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuUser(null);
  };

  const handleDeleteClick = () => {
    // Keep menuUser for the confirm dialog (closing the menu must not clear it)
    setAnchorEl(null);
    setDeleteConfirmOpen(true);
  };

  const handleResetPasswordClick = () => {
    // Keep menuUser for the confirm dialog
    setAnchorEl(null);
    setResetPasswordConfirmOpen(true);
  };

  const handleActivityClick = () => {
    if (menuUser) {
      setActivityUser(menuUser);
      setActivityOpen(true);
    }
    setAnchorEl(null);
  };

  const handleToggleStatus = () => {
    if (menuUser) {
      toggleUserStatusMutation.mutate({ id: menuUser.id, isActive: !menuUser.isActive });
    }
    handleMenuClose();
  };

  const users = usersData?.data?.users || [];
  const pagination = usersData?.data?.pagination || { total: 0, pages: 1 };

  const dualRoleOption = DUAL_ROLE_OPTIONS[formData.role];
  const holdsDualRole = Boolean(dualRoleOption && formData[dualRoleOption.flag]);

  const getRoleChip = (userOrRole: User | string) => {
    const role = typeof userOrRole === 'string' ? userOrRole : userOrRole.role;
    const config = roleConfig[role] || { label: role, color: '#757575', icon: <Person /> };
    const dualRoleFlag = FIRM_ADMIN_DUAL_ROLE_FLAGS[role];
    const rowHoldsDualRole =
      typeof userOrRole === 'string'
        ? false
        : Boolean(dualRoleFlag && userOrRole[dualRoleFlag]);
    const label = rowHoldsDualRole
      ? `${config.label} · ${FIRM_ADMIN_DUAL_ROLE_LABELS[role]}`
      : config.label;
    return (
      <Chip
        icon={config.icon}
        label={label}
        size="small"
        sx={{
          backgroundColor: `${config.color}15`,
          color: config.color,
          fontWeight: 500,
          '& .MuiChip-icon': {
            color: config.color,
          },
        }}
      />
    );
  };

  const getStatusChip = (isActive: boolean) => (
    <Chip
      icon={isActive ? <CheckCircle /> : <Block />}
      label={isActive ? 'Active' : 'Suspended'}
      size="small"
      color={isActive ? 'success' : 'error'}
      variant="outlined"
    />
  );

  return (
    <Box sx={{ p: 3 }}>
      {error && (
        <ListLoadErrorAlert
          resourceLabel="users"
          onRetry={() => refetch()}
        />
      )}
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
            Team Management
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Manage your firm's attorneys, paralegals, and support staff
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Upload />}
            onClick={() => {
              setImportCsv('');
              setImportResult(null);
              setImportOpen(true);
            }}
          >
            Import users
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog('create')}
            sx={{ px: 3 }}
          >
            Add Team Member
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {Object.entries(roleConfig).map(([role, config]) => {
          const count = users.filter((u: User) => u.role === role).length;
          return (
            <Grid item xs={6} md={3} key={role}>
              <Card sx={{ 
                height: '100%', 
                borderLeft: 4, 
                borderColor: config.color,
                cursor: 'pointer',
                transition: 'transform 0.2s',
                '&:hover': { transform: 'translateY(-2px)' },
              }}
              onClick={() => setRoleFilter(roleFilter === role ? '' : role)}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Box sx={{ 
                      p: 1, 
                      borderRadius: 1, 
                      backgroundColor: `${config.color}15`,
                      mr: 1.5,
                    }}>
                      {React.cloneElement(config.icon, { sx: { color: config.color } })}
                    </Box>
                    <Typography variant="h4" sx={{ fontWeight: 600 }}>{count}</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {config.label}s
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Role</InputLabel>
                <Select
                  value={roleFilter}
                  label="Role"
                  onChange={(e) => setRoleFilter(e.target.value)}
                >
                  <MenuItem value="">All Roles</MenuItem>
                  {Object.entries(roleConfig).map(([role, config]) => (
                    <MenuItem key={role} value={role}>{config.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Suspended</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={() => queryClient.invalidateQueries({ queryKey: ['users'] })}
              >
                Refresh
              </Button>
              {(roleFilter || statusFilter || searchQuery) && (
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => {
                    setSearchQuery('');
                    setRoleFilter('');
                    setStatusFilter('');
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <TableContainer>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : users.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <WorkOutline sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No team members found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {searchQuery || roleFilter || statusFilter 
                  ? 'Try adjusting your filters'
                  : 'Start by adding your first team member'}
              </Typography>
              {!searchQuery && !roleFilter && !statusFilter && (
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => handleOpenDialog('create')}
                >
                  Add Team Member
                </Button>
              )}
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Team Member</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last Login</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user: User) => (
                  <TableRow 
                    key={user.id} 
                    hover
                    sx={{ 
                      opacity: user.isActive ? 1 : 0.7,
                      '&:hover': { backgroundColor: 'action.hover' },
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ 
                          mr: 2, 
                          bgcolor: roleConfig[user.role]?.color || 'primary.main',
                        }}>
                          {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                            {user.firstName} {user.lastName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.email}
                          </Typography>
                          {user.specialization ? (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {user.specialization}
                            </Typography>
                          ) : null}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{getRoleChip(user)}</TableCell>
                    <TableCell>
                      {user.phone ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Phone sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                          <Typography variant="body2">{user.phone}</Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>{getStatusChip(user.isActive)}</TableCell>
                    <TableCell>
                      {user.lastLoginAt ? (
                        <Typography variant="body2" color="text.secondary">
                          {format(new Date(user.lastLoginAt), 'MMM d, yyyy')}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">Never</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleOpenDialog('edit', user)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="More actions">
                        <IconButton size="small" onClick={(e) => handleMenuOpen(e, user)}>
                          <MoreVert fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
        {users.length > 0 && (
          <TablePagination
            component="div"
            count={pagination.total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        )}
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => { handleOpenDialog('edit', menuUser!); handleMenuClose(); }}>
          <Edit fontSize="small" sx={{ mr: 1 }} /> Edit Details
        </MenuItem>
        <MenuItem onClick={handleActivityClick}>
          <History fontSize="small" sx={{ mr: 1 }} /> View Activity
        </MenuItem>
        <MenuItem onClick={handleResetPasswordClick}>
          <VpnKey fontSize="small" sx={{ mr: 1 }} /> Reset Password
        </MenuItem>
        <MenuItem onClick={handleToggleStatus}>
          {menuUser?.isActive ? (
            <>
              <Block fontSize="small" sx={{ mr: 1 }} /> Suspend User
            </>
          ) : (
            <>
              <CheckCircle fontSize="small" sx={{ mr: 1 }} /> Activate User
            </>
          )}
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          <Delete fontSize="small" sx={{ mr: 1 }} /> Delete User
        </MenuItem>
      </Menu>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' ? 'Add New Team Member' : `Edit ${selectedUser?.firstName} ${selectedUser?.lastName}`}
        </DialogTitle>
        <DialogContent dividers>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 3 }}>
            <Tab label="Basic Information" />
            <Tab label="Role & Permissions" />
          </Tabs>

          {activeTab === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={formData.firstName}
                  onChange={(e) => handleFormChange('firstName', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={formData.lastName}
                  onChange={(e) => handleFormChange('lastName', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleFormChange('email', e.target.value)}
                  required
                  disabled={dialogMode === 'edit'}
                  helperText={dialogMode === 'create' ? 'A temporary password will be sent to this email' : ''}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={formData.phone}
                  onChange={(e) => handleFormChange('phone', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Specialization"
                  value={formData.specialization}
                  onChange={(e) => handleFormChange('specialization', e.target.value)}
                  placeholder="e.g., Family Law, Corporate Law, Criminal Defense"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  value={formData.address}
                  onChange={(e) => handleFormChange('address', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="City"
                  value={formData.city}
                  onChange={(e) => handleFormChange('city', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="State"
                  value={formData.state}
                  onChange={(e) => handleFormChange('state', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="ZIP Code"
                  value={formData.zipCode}
                  onChange={(e) => handleFormChange('zipCode', e.target.value)}
                />
              </Grid>
            </Grid>
          )}

          {activeTab === 1 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>Select Role</Typography>
                <Grid container spacing={2}>
                  {Object.entries(roleConfig).map(([role, config]) => (
                    <Grid item xs={12} sm={6} key={role}>
                      <Card 
                        sx={{ 
                          p: 2, 
                          cursor: 'pointer',
                          border: 2,
                          borderColor: formData.role === role ? config.color : 'divider',
                          backgroundColor: formData.role === role ? `${config.color}08` : 'transparent',
                          transition: 'all 0.2s',
                          '&:hover': {
                            borderColor: config.color,
                          },
                        }}
                        onClick={() => handleFormChange('role', role)}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Box sx={{ 
                            p: 1, 
                            borderRadius: 1, 
                            backgroundColor: `${config.color}15`,
                            mr: 1.5,
                          }}>
                            {React.cloneElement(config.icon, { sx: { color: config.color } })}
                          </Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                            {config.label}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {config.description}
                        </Typography>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Grid>

              {dualRoleOption && (
                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ p: 2, bgcolor: 'action.hover' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(formData[dualRoleOption.flag])}
                          onChange={(e) => handleFormChange(dualRoleOption.flag, e.target.checked)}
                        />
                      }
                      label={dualRoleOption.label}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                      {dualRoleOption.description} Grants the same system access as Administrator
                      (users, settings, billing controls, financial reports, portal/trust admin).
                    </Typography>
                  </Card>
                </Grid>
              )}

              {!holdsDualRole && (
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Custom Permissions (Optional)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Override default role permissions with custom access controls
                </Typography>
                <Grid container spacing={1}>
                  {permissionOptions.map((permission) => (
                    <Grid item xs={12} sm={6} md={4} key={permission.value}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.permissions.includes(permission.value)}
                            onChange={() => handlePermissionToggle(permission.value)}
                            size="small"
                          />
                        }
                        label={<Typography variant="body2">{permission.label}</Typography>}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
              )}

              {dialogMode === 'edit' && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.isActive}
                        onChange={(e) => handleFormChange('isActive', e.target.checked)}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1">Active Account</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Suspended users cannot log in to the system
                        </Typography>
                      </Box>
                    }
                  />
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!formData.firstName || !formData.lastName || !formData.email || createUserMutation.isPending || updateUserMutation.isPending}
          >
            {(createUserMutation.isPending || updateUserMutation.isPending) ? (
              <CircularProgress size={24} />
            ) : dialogMode === 'create' ? (
              'Create User'
            ) : (
              'Save Changes'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setMenuUser(null);
        }}
      >
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone.
          </Alert>
          <Typography>
            Are you sure you want to delete <strong>{menuUser?.firstName} {menuUser?.lastName}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            The user account will be removed from the team list.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteConfirmOpen(false);
              setMenuUser(null);
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="error"
            onClick={() => menuUser && deleteUserMutation.mutate(menuUser.id)}
            disabled={!menuUser || deleteUserMutation.isPending}
          >
            {deleteUserMutation.isPending ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Confirmation Dialog */}
      <Dialog open={resetPasswordConfirmOpen} onClose={() => setResetPasswordConfirmOpen(false)}>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to reset the password for <strong>{menuUser?.firstName} {menuUser?.lastName}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            A new temporary password will be sent to their email address ({menuUser?.email}).
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetPasswordConfirmOpen(false)}>Cancel</Button>
          <Button 
            variant="contained"
            onClick={() => menuUser && resetPasswordMutation.mutate(menuUser.id)}
            disabled={resetPasswordMutation.isPending}
          >
            {resetPasswordMutation.isPending ? <CircularProgress size={24} /> : 'Reset Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk import dialog */}
      <Dialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Import users from CSV</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            One user per line: <code>email,firstName,lastName,role</code>
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            New passwords cannot reuse the last 5 passwords (server-enforced).
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={8}
            placeholder={'jane@firm.com,Jane,Smith,lawyer\nbob@firm.com,Bob,Jones,paralegal'}
            value={importCsv}
            onChange={(e) => {
              setImportCsv(e.target.value);
              setImportResult(null);
            }}
            sx={{ mb: 2, fontFamily: 'monospace' }}
          />

          {importCsv.trim() && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Preview ({importPreview.filter((r) => r.valid).length} valid /{' '}
                {importPreview.length} total)
              </Typography>
              {importPreview.length === 0 ? (
                <Alert severity="info">Paste CSV lines above to preview.</Alert>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Line</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {importPreview.map((row) => (
                      <TableRow key={row.line}>
                        <TableCell>{row.line}</TableCell>
                        <TableCell>{row.email || '—'}</TableCell>
                        <TableCell>
                          {row.firstName} {row.lastName}
                        </TableCell>
                        <TableCell>{row.role}</TableCell>
                        <TableCell>
                          {row.valid ? (
                            <Chip size="small" label="OK" color="success" />
                          ) : (
                            <Chip size="small" label={row.error} color="error" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          )}

          {importResult && (
            <Alert severity="info" sx={{ mt: 1 }}>
              Created: {importResult.created} · Skipped: {importResult.skipped}
              {importResult.errors.length > 0 && (
                <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                  {importResult.errors.map((e, i) => (
                    <li key={i}>
                      {e.line != null ? `Line ${e.line}` : 'Row'}
                      {e.email ? ` (${e.email})` : ''}: {e.message || e.error}
                    </li>
                  ))}
                </Box>
              )}
              {importResult.credentials && importResult.credentials.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Demo temporary passwords (copy to sign in):
                  </Typography>
                  {importResult.credentials.map((c) => (
                    <Box
                      key={c.email}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        fontFamily: 'monospace',
                        mb: 0.5,
                      }}
                    >
                      <span>
                        {c.email}: {c.temporaryPassword}
                      </span>
                      <IconButton
                        size="small"
                        onClick={() => {
                          navigator.clipboard.writeText(c.temporaryPassword);
                          toast.success('Password copied');
                        }}
                      >
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportOpen(false)}>Close</Button>
          <Button
            variant="contained"
            disabled={
              importSubmitting || importPreview.filter((r) => r.valid).length === 0
            }
            onClick={handleBulkImport}
          >
            {importSubmitting ? <CircularProgress size={24} /> : 'Import valid rows'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Activity Dialog */}
      <Dialog
        open={activityOpen}
        onClose={() => {
          setActivityOpen(false);
          setActivityUser(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Activity — {activityUser?.firstName} {activityUser?.lastName}
        </DialogTitle>
        <DialogContent dividers>
          {activityLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (activityData?.data?.auditLogs || []).length === 0 ? (
            <Alert severity="info">No recent activity recorded for this user.</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Resource</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>IP</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(activityData?.data?.auditLogs || []).map((log: any) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      {log.timestamp
                        ? format(new Date(log.timestamp), 'MMM d, yyyy HH:mm')
                        : '—'}
                    </TableCell>
                    <TableCell>{log.action}</TableCell>
                    <TableCell>
                      {log.resource}
                      {log.resourceId ? ` (${String(log.resourceId).slice(0, 8)}…)` : ''}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={log.status || 'ok'}
                        color={log.status === 'failed' || log.status === 'error' ? 'error' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{log.ipAddress || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setActivityOpen(false);
              setActivityUser(null);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Demo / local temporary password (not returned in production) */}
      <Dialog
        open={Boolean(tempPasswordInfo)}
        onClose={() => setTempPasswordInfo(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {tempPasswordInfo?.context === 'reset' ? 'Temporary password reset' : 'User created — temporary password'}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Email delivery is not available in this demo environment. Copy this password now —
            it will not be shown again.
          </Alert>
          {tempPasswordInfo?.name && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {tempPasswordInfo.name}
            </Typography>
          )}
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Email: <strong>{tempPasswordInfo?.email}</strong>
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mt: 1,
              p: 1.5,
              bgcolor: 'action.hover',
              borderRadius: 1,
              fontFamily: 'monospace',
              fontSize: '1.1rem',
              letterSpacing: 1,
            }}
          >
            <Box sx={{ flex: 1 }}>{tempPasswordInfo?.temporaryPassword}</Box>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ContentCopy />}
              onClick={() => {
                if (tempPasswordInfo?.temporaryPassword) {
                  navigator.clipboard.writeText(tempPasswordInfo.temporaryPassword);
                  toast.success('Password copied');
                }
              }}
            >
              Copy
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
            Sign in with this password, then set a permanent password on first login.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setTempPasswordInfo(null)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsersPage;
